import { motion } from 'framer-motion';
import { Wallet, FileSearch, CreditCard, LineChart, ArrowRight } from 'lucide-react';

const steps = [
  {
    icon: Wallet,
    title: '连接钱包',
    description: '连接 MetaMask 钱包并切换到 Monad 测试网',
    color: 'purple',
  },
  {
    icon: FileSearch,
    title: '选择参数',
    description: '选择币种、时间周期和预测长度',
    color: 'blue',
  },
  {
    icon: CreditCard,
    title: '支付查询',
    description: '使用 x402 协议支付 0.01 MON 费用',
    color: 'cyan',
  },
  {
    icon: LineChart,
    title: '获取预测',
    description: '查看 AI 生成的价格走势预测结果',
    color: 'green',
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            如何使用
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            简单四步，获取专业级加密货币价格预测
          </p>
        </motion.div>

        {/* 步骤流程 */}
        <div className="relative">
          {/* 连接线 */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-cyber-purple via-cyber-blue to-cyber-green -translate-y-1/2 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                {/* 步骤卡片 */}
                <div className="glass rounded-2xl p-6 text-center relative">
                  {/* 步骤编号 */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-r from-cyber-purple to-cyber-blue flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>

                  {/* 图标 */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getGradient(step.color)} flex items-center justify-center mx-auto mb-4 mt-4`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>

                  {/* 内容 */}
                  <h3 className="text-lg font-bold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {step.description}
                  </p>
                </div>

                {/* 箭头（除了最后一个） */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-4 -translate-y-1/2 z-20">
                    <ArrowRight className="w-8 h-8 text-cyber-purple/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* x402 协议说明 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-20"
        >
          <div className="glass rounded-3xl p-8 md:p-12 border border-cyber-purple/20">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
                  什么是 x402 协议？
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  x402 是一种基于 HTTP 402 状态码的去中心化微支付协议。
                  它允许服务提供者在不依赖中心化支付网关的情况下，
                  直接接收加密货币支付。
                </p>
                <ul className="space-y-3">
                  <FeatureItem text="无需注册或订阅，按需付费" />
                  <FeatureItem text="EIP-712 签名验证，安全可靠" />
                  <FeatureItem text="Nonce 防重放，保护资金安全" />
                  <FeatureItem text="支持链上结算和审计" />
                </ul>
              </div>

              {/* 流程图示 */}
              <div className="relative">
                <div className="bg-cyber-dark/50 rounded-2xl p-6 font-mono text-sm">
                  <div className="space-y-4">
                    <CodeLine prefix="1." text="GET /api/prediction" color="blue" />
                    <CodeLine prefix="→" text="402 Payment Required" color="yellow" />
                    <CodeLine prefix="2." text="Sign payment with wallet" color="purple" />
                    <CodeLine prefix="3." text="POST /api/prediction" color="blue" />
                    <CodeLine prefix=" " text="X-PAYMENT: [signed_payload]" color="cyan" />
                    <CodeLine prefix="→" text="200 OK + prediction data" color="green" />
                  </div>
                </div>

                {/* 装饰 */}
                <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-cyber-purple rounded-tr-lg" />
                <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-cyber-blue rounded-bl-lg" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FeatureItem({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-3">
      <div className="w-1.5 h-1.5 rounded-full bg-cyber-green" />
      <span className="text-gray-300">{text}</span>
    </li>
  );
}

function CodeLine({ prefix, text, color }: { prefix: string; text: string; color: string }) {
  const colors: Record<string, string> = {
    blue: 'text-cyber-blue',
    yellow: 'text-cyber-yellow',
    purple: 'text-cyber-purple',
    cyan: 'text-cyan-400',
    green: 'text-cyber-green',
  };

  return (
    <div className="flex gap-2">
      <span className="text-gray-600">{prefix}</span>
      <span className={colors[color]}>{text}</span>
    </div>
  );
}

function getGradient(color: string) {
  const gradients: Record<string, string> = {
    purple: 'from-cyber-purple/20 to-purple-900/20',
    blue: 'from-cyber-blue/20 to-blue-900/20',
    cyan: 'from-cyan-500/20 to-cyan-900/20',
    green: 'from-cyber-green/20 to-green-900/20',
  };
  return gradients[color] || gradients.purple;
}

